import { useEffect, useRef, useState } from 'react';

/**
 * Pull-to-refresh for window-scrolled pages (#2058).
 *
 * The PWA scrolls at the window/body level (no overflow container), and in
 * standalone display mode the browser provides no native pull-to-refresh — so
 * we implement our own. When the user drags down while already scrolled to the
 * very top and releases past `threshold`, `onRefresh` fires.
 *
 * Returns `pullDistance` (px, for a drag indicator) and `isRefreshing` (true
 * while the onRefresh promise is in flight).
 */
export function usePullToRefresh(
  onRefresh: () => Promise<void> | void,
  opts?: { threshold?: number; enabled?: boolean }
) {
  const threshold = opts?.threshold ?? 70;
  const enabled = opts?.enabled ?? true;
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs so the window listeners don't need to re-bind on every render / pull.
  const startY = useRef<number | null>(null);
  const pulling = useRef(false);
  const distRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;
    const MAX = threshold * 1.5;

    const setDist = (d: number) => {
      distRef.current = d;
      setPullDistance(d);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current || window.scrollY > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0].clientY;
      pulling.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        // Upward / no movement — not a pull.
        if (!pulling.current) startY.current = null;
        return;
      }
      if (window.scrollY > 0) {
        // Scrolled away from the top mid-gesture — abandon.
        startY.current = null;
        setDist(0);
        return;
      }
      pulling.current = true;
      // Apply resistance so the drag feels rubbery and can't run away.
      setDist(Math.min(MAX, dy * 0.5));
      if (e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (startY.current === null) return;
      const shouldRefresh = pulling.current && distRef.current >= threshold;
      startY.current = null;
      pulling.current = false;
      if (!shouldRefresh) {
        setDist(0);
        return;
      }
      refreshingRef.current = true;
      setIsRefreshing(true);
      setDist(threshold);
      try {
        await onRefreshRef.current();
      } finally {
        refreshingRef.current = false;
        setIsRefreshing(false);
        setDist(0);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [threshold, enabled]);

  return { pullDistance, isRefreshing };
}
